import type { ReactNode } from 'react';
import { Icon, Tag } from '@dayon/design-system';
import { DOCUMENT_CONFIDENCE_KEY } from '@dayon/contracts';
import {
  adviceWords,
  calendarDay,
  confidenceLabel,
  decisionWho,
  disagreementWords,
  laterAnswerDisagrees,
  laterAnswerWords,
  livenessWords,
  momentWords,
  outcomeTone,
  outcomeWords,
  providerWords,
  screeningWords,
  sourceWords,
  unscreenedApprovalWords,
  type IdentityRecordFacts,
} from './record-words.ts';

/**
 * ONE IDENTITY CHECK, AS IT WAS DECIDED — what an inspector or a support agent asks for.
 *
 * The outcome and who checked the document; the name and the date of birth with where each
 * came from; the liveness result; who decided and when, with their note; and the confidences
 * the service gave. Rendered on the player's KYC tab for every record, and at the top of a
 * decided case on Identity checks, so both say the same thing about the same record.
 *
 * `evidence={false}` leaves out the liveness, the screening, CIRA's advice and the confidences,
 * for the case view, which already shows all four in its own panels.
 *
 * ── THE SCREENING, AND WHAT CIRA SAID LATER — 15 Oct 2026 ──────────────────
 *
 * CIRA screens nobody on this account for sanctions or PEP since 15 Oct 2026, 8:22 PM, so a
 * check reads "Not screened" in those words, and a person's approval of one says that it
 * screened no one either. CIRA's analyst decides a PEP match in a later notice; that answer is
 * shown beside the decision, and where it advises rejecting a player this platform verified,
 * the record opens with that, plainly. Nothing changes because of it, and the record says so.
 */
export function RecordFactsList({
  record,
  evidence = true,
}: {
  record: IdentityRecordFacts;
  evidence?: boolean;
}) {
  const { decision } = record;
  /* The document-level figure first: it is the one CIRA actually gives. */
  const confidences = Object.entries(record.confidence).toSorted(([a], [b]) =>
    a === DOCUMENT_CONFIDENCE_KEY ? -1 : b === DOCUMENT_CONFIDENCE_KEY ? 1 : a.localeCompare(b),
  );

  const later = record.laterAnswer;
  const unscreenedApproval = unscreenedApprovalWords(record);

  return (
    <>
      {laterAnswerDisagrees(record) && (
        <div className="notice" style={{ alignItems: 'flex-start' }}>
          <Icon name="warning" />
          <div>{disagreementWords(record)}</div>
        </div>
      )}
    <dl className="stack" style={{ margin: 0 }}>
      <Row label="Outcome">
        <Tag tone={outcomeTone(record.outcome)}>{outcomeWords(record)}</Tag>
      </Row>
      <Row label="Checked by">{providerWords(record.provider)}</Row>
      <Row label="Name on the ID">
        <Sourced
          value={record.fullName}
          source={sourceWords(record.fullName, record.sources.fullName, record)}
        />
      </Row>
      <Row label="Date of birth on the ID">
        <Sourced
          value={record.dateOfBirth === null ? null : calendarDay(record.dateOfBirth)}
          source={sourceWords(record.dateOfBirth, record.sources.dateOfBirth, record)}
        />
      </Row>
      {evidence && <Row label="Liveness check">{livenessWords(record.liveness)}</Row>}
      {evidence && (
        <Row label="Sanctions and PEP screening">
          {screeningWords(record.screening, record.provider, record.noCiraCheckOnRecord)}
        </Row>
      )}
      {evidence && record.advice !== null && (
        <Row label="CIRA’s advice">{adviceWords(record.advice)}</Row>
      )}
      <Row label="Decided">
        {decision === null ? (
          /* A replaced attempt is never decided: the newer check is the one a person sees. */
          <span className="text-subtle">
            {record.current ? 'Not decided yet' : 'Never decided — a newer check replaced it'}
          </span>
        ) : (
          <>
            <div>
              {decisionWho(decision, record.provider)} · {momentWords(decision.at)}
            </div>
            {decision.reason !== null && <div className="text-subtle">Reason: {decision.reason}</div>}
            {decision.note !== null && <div className="text-subtle">Note: “{decision.note}”</div>}
          </>
        )}
        {unscreenedApproval !== null && <div className="text-subtle">{unscreenedApproval}</div>}
      </Row>
      {later !== null && (
        <Row label="CIRA’s later answer">
          <div>{momentWords(later.at)}</div>
          <div className="text-subtle">{laterAnswerWords(later)}</div>
        </Row>
      )}
      {evidence && (
        <Row label="Confidence">
          {confidences.length === 0 ? (
            <span className="text-subtle">None given</span>
          ) : (
            <span className="row" style={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {confidences.map(([key, value]) => (
                <Tag key={key} tone="neutral">
                  {confidenceLabel(key)} {value.toFixed(2)}
                </Tag>
              ))}
            </span>
          )}
        </Row>
      )}
    </dl>
    </>
  );
}

/* A label left and its value right, wrapping inside the row on a phone rather than past it. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="def-row">
      <dt>{label}</dt>
      <dd style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</dd>
    </div>
  );
}

/* A value and, beneath it, where it came from — or that nothing is recorded. */
function Sourced({ value, source }: { value: string | null; source: string | null }) {
  if (value === null) return <span className="text-subtle">Nothing recorded</span>;
  return (
    <>
      <div>{value}</div>
      {source !== null && <div className="text-subtle">{source}</div>}
    </>
  );
}
